/**
 * Kind-specific decision affordances + shared action field primitives.
 *
 * Moved out of `dialogs/PendingDecisionsPanel.tsx` so the field-scoped
 * Inspector can render decisions inline (no separate drawer). The shape
 * is unchanged — `ALLOWED_ACTIONS_BY_KIND` from `mappingApi.ts` gates
 * which actions surface per kind; the operator-facing labels and
 * tooltips also live here.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  ALLOWED_ACTIONS_BY_KIND,
  type DecisionKind,
  type PendingDecision,
  type PolicyChoice,
  type ResolveAction,
} from '../../../services/mappingApi';

// ---------------------------------------------------------------------------
// Visual helpers
// ---------------------------------------------------------------------------

export const SeverityIcon: React.FC<{ severity: 'error' | 'warning' | 'info' }> = ({
  severity,
}) => {
  if (severity === 'error') return <ErrorOutlineIcon color="error" fontSize="small" />;
  if (severity === 'warning') return <WarningAmberIcon color="warning" fontSize="small" />;
  return <InfoOutlinedIcon color="info" fontSize="small" />;
};

export const KIND_LABELS: Record<DecisionKind, { label: string; help: string }> = {
  ambiguous_candidates: {
    label: 'Ambiguous',
    help: 'Multiple plausibly-correct sources; pick one or provide your own.',
  },
  missing_information: {
    label: 'Missing info',
    help: 'No defensible source/transformation found. Provide a value or pick a policy.',
  },
  schema_policy_conflict: {
    label: 'Schema conflict',
    help: 'Source data conflicts with a schema rule. Override or apply policy.',
  },
  source_data_quality: {
    label: 'Bad source data',
    help: 'Source data is unrecoverable. Pick a data-quality policy.',
  },
  mapper_repeated_rejection: {
    label: 'Needs your input',
    // Plain-language only — no platform jargon ("Mapper",
    // "validator", "expression", "policy") in operator surfaces.
    // Detail panels carry the technical evidence further down.
    help:
      'We tried filling this in automatically but the results ' +
      'didn’t look right. Please tell us what to use.',
  },
};

// ---------------------------------------------------------------------------
// Kind dispatch
// ---------------------------------------------------------------------------

export interface ActionSubProps {
  decision: PendingDecision;
  allowed: ResolveAction['action'][];
  disabled: boolean;
  onResolve: (action: ResolveAction) => void;
  /**
   * Set of rule names already overridden on this field
   * (`recipe.validator_overrides[target_table][target_field]`).
   * Used by `SchemaPolicyConflictItem` to hide `accept_override` when
   * the specific rule the decision references has already been
   * overridden — prevents double-overrides (plan §G/13).
   */
  overriddenRuleNames?: Set<string>;
}

export const KindSpecificActions: React.FC<{
  decision: PendingDecision;
  disabled: boolean;
  onResolve: (action: ResolveAction) => void;
  overriddenRuleNames?: Set<string>;
}> = ({ decision, disabled, onResolve, overriddenRuleNames }) => {
  const allowed = ALLOWED_ACTIONS_BY_KIND[decision.kind] ?? [];
  const props: ActionSubProps = { decision, allowed, disabled, onResolve, overriddenRuleNames };
  switch (decision.kind) {
    case 'ambiguous_candidates':
      return <AmbiguousCandidatesItem {...props} />;
    case 'missing_information':
      return <MissingInformationItem {...props} />;
    case 'schema_policy_conflict':
      return <SchemaPolicyConflictItem {...props} />;
    case 'source_data_quality':
      return <SourceDataQualityItem {...props} />;
    case 'mapper_repeated_rejection':
      // Same action surface as missing_information (provide
      // constant / expression / apply policy / defer / dismiss).
      // ``ALLOWED_ACTIONS_BY_KIND`` adds ``pick_candidate`` for
      // this kind; the MissingInformationItem panel gates each
      // action against ``allowed`` so the extra entry surfaces
      // when present without panel-side branching.
      return <MissingInformationItem {...props} />;
    default:
      return (
        <Alert severity="info" sx={{ mt: 1 }}>
          Unknown decision kind: {String(decision.kind)}. Use Defer or Dismiss.
        </Alert>
      );
  }
};

// ---------------------------------------------------------------------------
// Per-kind action panels
// ---------------------------------------------------------------------------

export const AmbiguousCandidatesItem: React.FC<ActionSubProps> = ({
  decision,
  allowed,
  disabled,
  onResolve,
}) => {
  const candidates = decision.payload.candidate_sources ?? [];
  return (
    <>
      {candidates.length > 0 ? (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Pick a source candidate:
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
            {candidates.map((c, idx) => (
              <Tooltip
                key={`${c.source_file}.${c.source_field}`}
                title={`similarity: ${(c.similarity * 100).toFixed(0)}%`}
              >
                <Button
                  size="small"
                  variant="outlined"
                  disabled={disabled || !allowed.includes('pick_candidate')}
                  onClick={() =>
                    onResolve({ action: 'pick_candidate', candidate_index: idx })
                  }
                >
                  {c.source_file}.{c.source_field}
                </Button>
              </Tooltip>
            ))}
          </Stack>
        </Box>
      ) : (
        <Alert severity="warning" sx={{ mt: 1 }}>
          No candidates persisted on this decision. Use the expression escape hatch or defer.
        </Alert>
      )}
      {allowed.includes('provide_expression') && (
        <ExpressionField disabled={disabled} onResolve={onResolve} />
      )}
    </>
  );
};

export const MissingInformationItem: React.FC<ActionSubProps> = ({
  decision,
  allowed,
  disabled,
  onResolve,
}) => {
  const isNullable = decision.payload.is_nullable !== false;
  return (
    <>
      {allowed.includes('provide_constant') && (
        <ConstantField disabled={disabled} onResolve={onResolve} />
      )}
      {allowed.includes('provide_expression') && (
        <ExpressionField disabled={disabled} onResolve={onResolve} />
      )}
      {allowed.includes('apply_policy') && (
        <PolicyChooser disabled={disabled} isNullable={isNullable} onResolve={onResolve} />
      )}
    </>
  );
};

export const SchemaPolicyConflictItem: React.FC<ActionSubProps> = ({
  decision,
  allowed,
  disabled,
  onResolve,
  overriddenRuleNames,
}) => {
  const candidates = decision.payload.candidate_sources ?? [];
  const isNullable = decision.payload.is_nullable !== false;
  // Plan §G/13: if the rule this decision references is already in
  // `validator_overrides`, hide `accept_override` — operator must
  // resolve via candidate / policy / dismiss / defer instead.
  const decisionRuleKey =
    (decision.payload.rule as string | undefined) ??
    (decision.payload.validator_rules?.[0]?.rule as string | undefined) ??
    (decision.payload.validator_rules?.[0]?.type as string | undefined);
  const ruleAlreadyOverridden = Boolean(
    decisionRuleKey && overriddenRuleNames?.has(decisionRuleKey),
  );
  return (
    <>
      {allowed.includes('accept_override') && !ruleAlreadyOverridden && (
        <OverrideField disabled={disabled} onResolve={onResolve} />
      )}
      {ruleAlreadyOverridden && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Rule <strong>{decisionRuleKey}</strong> is already overridden on this
          field — pick a candidate, apply a policy, or dismiss.
        </Alert>
      )}
      {candidates.length > 0 && allowed.includes('pick_candidate') && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Pick from allowed candidates instead:
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
            {candidates.map((c, idx) => (
              <Button
                key={`${c.source_file}.${c.source_field}`}
                size="small"
                variant="outlined"
                disabled={disabled}
                onClick={() => onResolve({ action: 'pick_candidate', candidate_index: idx })}
              >
                {c.source_file}.{c.source_field}
              </Button>
            ))}
          </Stack>
        </Box>
      )}
      {allowed.includes('apply_policy') && (
        <PolicyChooser disabled={disabled} isNullable={isNullable} onResolve={onResolve} />
      )}
    </>
  );
};

export const SourceDataQualityItem: React.FC<ActionSubProps> = ({
  decision,
  allowed,
  disabled,
  onResolve,
}) => {
  const isNullable = decision.payload.is_nullable !== false;
  return (
    <>
      {allowed.includes('apply_policy') && (
        <PolicyChooser disabled={disabled} isNullable={isNullable} onResolve={onResolve} />
      )}
      {allowed.includes('provide_expression') && (
        <ExpressionField disabled={disabled} onResolve={onResolve} />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Shared action field primitives
// ---------------------------------------------------------------------------

export const ConstantField: React.FC<{
  disabled: boolean;
  onResolve: (action: ResolveAction) => void;
}> = ({ disabled, onResolve }) => {
  const [value, setValue] = useState<string>('');
  return (
    <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 0.5 }}>
      <TextField
        size="small"
        label="constant value"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={disabled}
        fullWidth
      />
      <Button
        size="small"
        variant="contained"
        disabled={disabled || !value.trim()}
        onClick={() => onResolve({ action: 'provide_constant', value })}
      >
        provide
      </Button>
    </Stack>
  );
};

export const ExpressionField: React.FC<{
  disabled: boolean;
  onResolve: (action: ResolveAction) => void;
}> = ({ disabled, onResolve }) => {
  const [expression, setExpression] = useState<string>('');
  return (
    <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 0.5 }}>
      <TextField
        size="small"
        label="SQL expression (e.g. CASE WHEN ... ELSE ... END)"
        value={expression}
        onChange={(e) => setExpression(e.target.value)}
        disabled={disabled}
        multiline
        minRows={1}
        maxRows={4}
        fullWidth
      />
      <Button
        size="small"
        variant="outlined"
        disabled={disabled || !expression.trim()}
        onClick={() =>
          onResolve({ action: 'provide_expression', expression: expression.trim() })
        }
      >
        apply expr
      </Button>
    </Stack>
  );
};

export const PolicyChooser: React.FC<{
  disabled: boolean;
  isNullable: boolean;
  onResolve: (action: ResolveAction) => void;
}> = ({ disabled, isNullable, onResolve }) => {
  const [policy, setPolicy] = useState<PolicyChoice>('use_sentinel');
  const [sentinelValue, setSentinelValue] = useState<string>('');
  const [nullReason, setNullReason] = useState<string>('');

  const canSubmit = (() => {
    if (disabled) return false;
    if (policy === 'use_sentinel') return Boolean(sentinelValue.trim());
    if (policy === 'set_null_with_reason')
      return isNullable && Boolean(nullReason.trim());
    return true;
  })();

  const submit = () => {
    if (policy === 'use_sentinel') {
      onResolve({ action: 'apply_policy', policy: 'use_sentinel', sentinel_value: sentinelValue });
    } else if (policy === 'set_null_with_reason') {
      onResolve({ action: 'apply_policy', policy: 'set_null_with_reason', null_reason: nullReason });
    } else {
      onResolve({ action: 'apply_policy', policy });
    }
  };

  return (
    <Box sx={{ mt: 1, mb: 1, p: 1, border: '1px dashed rgba(0,0,0,0.16)', borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Apply data-quality policy:
      </Typography>
      <FormControl size="small" sx={{ mt: 0.5, display: 'block' }}>
        <Select
          value={policy}
          onChange={(e) => setPolicy(e.target.value as PolicyChoice)}
          disabled={disabled}
          fullWidth
        >
          <MenuItem value="use_sentinel">use sentinel value</MenuItem>
          <MenuItem value="set_null_with_reason" disabled={!isNullable}>
            set null (with reason){!isNullable ? ' — column non-nullable' : ''}
          </MenuItem>
          <MenuItem value="drop_rows">drop offending rows</MenuItem>
          <MenuItem value="fail_load">fail the load</MenuItem>
        </Select>
      </FormControl>
      {policy === 'use_sentinel' && (
        <TextField
          size="small"
          label="sentinel value"
          value={sentinelValue}
          onChange={(e) => setSentinelValue(e.target.value)}
          disabled={disabled}
          fullWidth
          sx={{ mt: 0.5 }}
        />
      )}
      {policy === 'set_null_with_reason' && (
        <TextField
          size="small"
          label="null reason"
          value={nullReason}
          onChange={(e) => setNullReason(e.target.value)}
          disabled={disabled || !isNullable}
          fullWidth
          sx={{ mt: 0.5 }}
        />
      )}
      <Box sx={{ textAlign: 'right', mt: 0.5 }}>
        <Button size="small" variant="contained" disabled={!canSubmit} onClick={submit}>
          apply policy
        </Button>
      </Box>
    </Box>
  );
};

export const OverrideField: React.FC<{
  disabled: boolean;
  onResolve: (action: ResolveAction) => void;
}> = ({ disabled, onResolve }) => {
  const [rationale, setRationale] = useState<string>('');
  return (
    <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 0.5 }}>
      <TextField
        size="small"
        label="why are you overriding the schema rule?"
        value={rationale}
        onChange={(e) => setRationale(e.target.value)}
        disabled={disabled}
        multiline
        minRows={1}
        maxRows={3}
        fullWidth
      />
      <Button
        size="small"
        variant="contained"
        color="warning"
        disabled={disabled || !rationale.trim()}
        onClick={() =>
          onResolve({ action: 'accept_override', rationale: rationale.trim() })
        }
      >
        accept override
      </Button>
    </Stack>
  );
};

export const UniversalActions: React.FC<{
  allowed: ResolveAction['action'][];
  disabled: boolean;
  onResolve: (action: ResolveAction) => void;
}> = ({ allowed, disabled, onResolve }) => {
  const [deferNote, setDeferNote] = useState<string>('');
  const [dismissRationale, setDismissRationale] = useState<string>('');
  const [showDismiss, setShowDismiss] = useState<boolean>(false);
  const [showDefer, setShowDefer] = useState<boolean>(false);
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ mt: 1, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 0.5 }}
    >
      {allowed.includes('defer') && (
        <>
          {showDefer ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <TextField
                size="small"
                label="note (optional)"
                value={deferNote}
                onChange={(e) => setDeferNote(e.target.value)}
                disabled={disabled}
              />
              <Button
                size="small"
                color="inherit"
                disabled={disabled}
                onClick={() =>
                  onResolve({ action: 'defer', note: deferNote.trim() || undefined })
                }
              >
                confirm
              </Button>
              <Button size="small" color="inherit" onClick={() => setShowDefer(false)}>
                cancel
              </Button>
            </Stack>
          ) : (
            <Tooltip title="Defer for later triage; decision remains in queue.">
              <Button
                size="small"
                color="inherit"
                disabled={disabled}
                onClick={() => setShowDefer(true)}
              >
                defer
              </Button>
            </Tooltip>
          )}
        </>
      )}
      {allowed.includes('dismiss') && (
        <>
          {showDismiss ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <TextField
                size="small"
                label="dismiss rationale"
                value={dismissRationale}
                onChange={(e) => setDismissRationale(e.target.value)}
                disabled={disabled}
                required
              />
              <Button
                size="small"
                color="inherit"
                disabled={disabled || !dismissRationale.trim()}
                onClick={() =>
                  onResolve({ action: 'dismiss', rationale: dismissRationale.trim() })
                }
              >
                confirm
              </Button>
              <Button size="small" color="inherit" onClick={() => setShowDismiss(false)}>
                cancel
              </Button>
            </Stack>
          ) : (
            <Button
              size="small"
              color="inherit"
              disabled={disabled}
              onClick={() => setShowDismiss(true)}
            >
              dismiss
            </Button>
          )}
        </>
      )}
    </Stack>
  );
};

// Render-friendly chip listing the kind of decision (label only).
export const DecisionKindChip: React.FC<{ kind: DecisionKind; size?: 'small' | 'medium' }> = ({
  kind,
  size = 'small',
}) => {
  const meta = KIND_LABELS[kind] ?? { label: String(kind), help: '' };
  return (
    <Tooltip title={meta.help}>
      <Chip label={meta.label} size={size} color="info" />
    </Tooltip>
  );
};
